import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { X, Pen } from 'lucide-react';
import { z } from 'zod';

import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api/client';
import { getApiErrorMessage } from '@/lib/utils';

interface SettingsActionProps {
  id?: number | string;
  title: string;
  apiEndpoint: string;
  queryKey: string;
  placeholder?: string;
  successMessageCreate?: string;
  successMessageUpdate?: string;
}

export function SettingsAction({
  id,
  title,
  apiEndpoint,
  queryKey,
  placeholder,
  successMessageCreate,
  successMessageUpdate,
}: SettingsActionProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const schema = z.object({
    name: z.string().min(1, t('nameIsRequired') || 'Name is required'),
  });

  type FormValues = z.infer<typeof schema>;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
    },
  });

  const showData = async (showId: number | string) => {
    const res = await api.get(`/${apiEndpoint}/${showId}`);
    return res;
  };

  const createData = async (data: FormValues) => {
    return api.post(`/${apiEndpoint}`, data);
  };

  const updateData = async ({ updateId, data }: { updateId: number | string; data: FormValues }) => {
    return api.patch(`/${apiEndpoint}/${updateId}`, data);
  };

  const createMutation = useMutation({
    mutationFn: createData,
    onSuccess: () => {
      toast.success(successMessageCreate || t('createdSuccess', { item: title }));
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      form.reset();
      setOpen(false);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('creatingFailed', { message: 'Unknown error' })));
    },
  });

  const updateMutation = useMutation({
    mutationFn: updateData,
    onSuccess: () => {
      toast.success(successMessageUpdate || t('updatedSuccess', { item: title }));
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      setOpen(false);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, t('updatingError', { message: 'Unknown error' })));
    },
  });

  const showMutation = useMutation({
    mutationFn: showData,
    onSuccess: (data) => {
      form.reset({
        name: data.name || '',
      });
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const { isSubmitting } = form.formState;

  useEffect(() => {
    if (open && id) {
      showMutation.mutate(id);
    }
  }, [open, id]);

  const onSubmit = async (values: FormValues, action: 'create' | 'update' = 'create') => {
    const payload = { ...values };
    if (action === 'update' && id) {
      updateMutation.mutate({ updateId: id, data: payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isPending = createMutation.isPending || updateMutation.isPending;

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        {id ? (
          <Button size="sm" variant="outline" className="rounded-r-none">
            <Pen />
          </Button>
        ) : (
          <Button>
            {t('new')} <span className="lowercase">{title}</span>
          </Button>
        )}
      </AlertDialogTrigger>

      <AlertDialogContent className="p-6 max-w-lg">
        <div className="flex items-center justify-between border-b pb-4 border-[#D9D9D9]">
          <AlertDialogTitle className="text-xl font-semibold">
            {id ? t('update') : t('new')} <span className="lowercase">{title}</span>
          </AlertDialogTitle>
          <AlertDialogDescription asChild>
            <button onClick={() => setOpen(false)} aria-label={t('closeDialog')}>
              <X size={24} />
            </button>
          </AlertDialogDescription>
        </div>

        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) => onSubmit(values, 'create'))}
            className="mt-4 flex flex-col gap-4"
            noValidate
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel>
                    {t('name')}{' '}
                    <sup className="text-red-500">*</sup>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder={placeholder || t('enterName')} {...field} />
                  </FormControl>
                  {form.formState.errors.name && (
                    <FormMessage>{form.formState.errors.name.message}</FormMessage>
                  )}
                </FormItem>
              )}
            />

            <AlertDialogFooter className="flex justify-end gap-2 mt-4">
              <AlertDialogCancel onClick={() => setOpen(false)}>
                {t('cancel')}
              </AlertDialogCancel>

              {id ? (
                <Button
                  type="button"
                  disabled={isSubmitting || isPending}
                  onClick={form.handleSubmit((values) => onSubmit(values, 'update'))}
                >
                  {isPending ? t('saving') : `${t('save')} ${title}`}
                </Button>
              ) : (
                <Button
                  type="button"
                  disabled={isSubmitting || isPending}
                  onClick={form.handleSubmit((values) => onSubmit(values, 'create'))}
                >
                  {isPending ? t('saving') : t('save')}
                </Button>
              )}
            </AlertDialogFooter>
          </form>
        </Form>
      </AlertDialogContent>
    </AlertDialog>
  );
}
